<script setup lang="ts">
  import renderRichText from '~/utils/renderSbRichText'

  interface IGlobalAlert {
    isBold: boolean
    closable: boolean
    showDefaultIcon: boolean
    type: null | undefined | string
    content?: any
    icon: {
      alt: string
      source?: string
      filename?: string
    } | null
  }

  const props = defineProps<{ alert: IGlobalAlert }>()
  const defaultInfoIcon = '/icons/ic-outline-info.svg'
  const defaultCloseIcon = '/icons/ic-round-close.svg'
  const text = computed(() => renderRichText(props.alert.content))
  const iconSource = computed(() => props.alert.icon?.source || props.alert.icon?.filename || '')
  const hasCustomIcon = computed(() => !!iconSource.value)
  const isDefaultIconEnabled = computed(() => {
    const raw = props.alert.showDefaultIcon as unknown

    return typeof raw === 'string' ? raw.toLowerCase() === 'true' : !!raw
  })
  const isClosable = computed(() => {
    const raw = props.alert.closable as unknown

    return typeof raw === 'string' ? raw.toLowerCase() === 'true' : !!raw
  })
  const useDefaultIcon = computed(() => isDefaultIconEnabled.value)
  const useCustomIcon = computed(() => !isDefaultIconEnabled.value && hasCustomIcon.value)
  const alertTypeModifier = computed(() => {
    const raw = props.alert.type?.toLowerCase().trim()
    const normalizedType = raw || 'info'

    if (normalizedType === 'info' || normalizedType === 'warning' || normalizedType === 'danger') {
      return `global-alert--${normalizedType}`
    }

    return null
  })

  const isVisible = ref<boolean>(true)
  const onClose = () => {
    if (!isClosable.value) {
      return
    }

    isVisible.value = false
  }

  watch(
    () => props.alert,
    () => {
      // Show alert again when CMS data changes (e.g. locale switch/new alert content).
      isVisible.value = true
    },
    { deep: true }
  )
</script>

<template>
  <div
    v-if="isVisible"
    class="global-alert"
    :class="alertTypeModifier"
  >
    <div class="global-alert__container">
      <div class="global-alert__content">
        <NuxtImg
          v-if="useDefaultIcon"
          :src="defaultInfoIcon"
          width="24"
          height="24"
          alt=""
          aria-hidden="true"
          class="global-alert__icon"
        />
        <NuxtImg
          v-else-if="useCustomIcon"
          width="24"
          height="24"
          :alt="alert.icon?.alt"
          :src="iconSource"
          provider="storyblok"
          class="global-alert__icon"
        />
        <div
          class="global-alert__text"
          :class="{ 'global-alert__text--bold': alert.isBold }"
          v-html="text"
        />
      </div>
      <button
        v-if="isClosable"
        :aria-label="$t('aria.label.close')"
        class="global-alert__close"
        @click="onClose"
      >
        <NuxtImg
          :src="defaultCloseIcon"
          width="24"
          height="24"
          alt=""
          aria-hidden="true"
          class="global-alert__close-icon"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .global-alert {
    padding-top: 1rem;
    padding-bottom: 1rem;
    color: $bt-blue;

    &--info {
      background-color: $bg-light-info;
    }

    &--warning {
      background-color: $bg-light-warning;
    }

    &--danger {
      background-color: $bg-light-error;
    }

    &__container {
      align-items: center;
      display: flex;
      gap: 1rem;
      justify-content: space-between;
      margin: auto;
      max-width: 1440px;
      padding: 0 1rem;
    }

    &__content {
      align-items: flex-start;
      display: flex;
      gap: 1rem;
      min-width: 0;
    }

    &__icon {
      flex: 0 0 1.5rem;
      height: 1.5rem;
      width: 1.5rem;
    }

    &__close {
      align-self: flex-start;
      background: none;
      border: none;
      cursor: pointer;
      flex: 0 0 auto;
      padding: 0;
    }

    &__close-icon {
      height: 1.5rem;
      opacity: 0.3;
      width: 1.5rem;
    }

    &__text {
      color: $bt-blue;
      font-size: 1rem;
      line-height: 1.5rem;

      &--bold {
        font-weight: 700;
      }

      :deep(p) {
        margin: 0 !important;
        text-decoration: none;
      }
    }
  }
</style>
